import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type NotifKind = 'assign' | 'mention' | 'breach' | 'comment';

export type Notif = {
  id: number;
  kind: NotifKind;
  text: string;
  at: number;
  ticketRef?: string;
  read: boolean;
};

const Ctx = createContext<{
  notifs: Notif[];
  push: (n: Omit<Notif, 'id' | 'at' | 'read'>) => void;
  markAllRead: () => void;
  unread: number;
} | null>(null);

let counter = 0;

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifs, setNotifs] = useState<Notif[]>([]);

  const push = useCallback((n: Omit<Notif, 'id' | 'at' | 'read'>) => {
    setNotifs((prev) => [{ ...n, id: ++counter, at: Date.now(), read: false }, ...prev].slice(0, 50));
  }, []);

  const markAllRead = useCallback(() => setNotifs((prev) => prev.map((n) => ({ ...n, read: true }))), []);

  const unread = useMemo(() => notifs.filter((n) => !n.read).length, [notifs]);

  return <Ctx.Provider value={{ notifs, push, markAllRead, unread }}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}
